import React, { useEffect, useMemo, useState } from "react";
import { D, st } from "../../constants";
import { LABOR_RATES, MATERIAL_MARKUP, CONTROLLER_TIERS, currency } from "./ledRates";

export default function LedControllerTool({ onCalcChange }) {
  const [tierId, setTierId] = useState("caseta");
  const [zones, setZones] = useState(1);

  const tier = CONTROLLER_TIERS.find((t) => t.id === tierId) || CONTROLLER_TIERS[0];

  const { materialCost, materialSell, laborHours, laborCost, total } = useMemo(() => {
    const z = Math.max(0, Number(zones) || 0);
    const materialCost = z * tier.costPerZone;
    const materialSell = materialCost * (1 + MATERIAL_MARKUP);
    const laborHours = (z * tier.programMinPerZone) / 60;
    const laborCost = laborHours * LABOR_RATES.controlsTech;
    return { materialCost, materialSell, laborHours, laborCost, total: materialSell + laborCost };
  }, [zones, tier]);

  useEffect(() => {
    onCalcChange?.({ materialCost: materialSell, laborCost, total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialSell, laborCost, total]);

  return (
    <div style={st.card}>
      <div style={st.h2}>Control System Selector</div>
      <div style={{ fontSize: 13, color: D.muted, marginBottom: 16 }}>
        Price out a lighting control system on its own, or feed the total into a bid.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10, marginBottom: 20 }}>
        {CONTROLLER_TIERS.map((t) => {
          const active = t.id === tierId;
          return (
            <div
              key={t.id}
              onClick={() => setTierId(t.id)}
              style={{
                cursor: "pointer",
                padding: 14,
                borderRadius: 8,
                border: `1px solid ${active ? D.accent : D.border}`,
                background: active ? D.input : D.card,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontWeight: 500, color: D.text, fontSize: 13 }}>{t.label}</span>
                <span style={{ fontFamily: "monospace", fontSize: 12, color: D.accent }}>
                  {currency(t.costPerZone)}/zone
                </span>
              </div>
              <div style={{ fontSize: 11, color: D.muted, lineHeight: 1.4 }}>{t.description}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <label style={{ ...st.label, marginBottom: 0 }}>Number of zones</label>
        <input
          type="number"
          min="0"
          value={zones}
          onChange={(e) => setZones(e.target.value)}
          style={{ ...st.input, width: 90 }}
        />
      </div>

      <table style={st.table}>
        <tbody>
          <tr>
            <td style={{ ...st.td, color: D.muted }}>
              {tier.label} — controller/switch (× {zones}), with {(MATERIAL_MARKUP * 100).toFixed(0)}% markup
            </td>
            <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(materialSell)}</td>
          </tr>
          <tr>
            <td style={{ ...st.td, color: D.muted }}>
              Programming & commissioning ({laborHours.toFixed(2)} hrs @ {currency(LABOR_RATES.controlsTech)}/hr)
            </td>
            <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(laborCost)}</td>
          </tr>
          <tr>
            <td style={{ ...st.td, borderBottom: "none", fontWeight: 600, color: D.text }}>Total</td>
            <td style={{ ...st.td, borderBottom: "none", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: D.accent, fontSize: 15 }}>
              {currency(total)}
            </td>
          </tr>
        </tbody>
      </table>

      {tierId === "dmx" && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: D.warning + "1a", border: `1px solid ${D.warning}55`, fontSize: 12, color: D.muted }}>
          90 min/zone is a floor, not a ceiling. Multi-universe, pixel-mapped, or
          Art-Net/sACN jobs need custom programming hours — price those separately.
        </div>
      )}
    </div>
  );
}
